import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import format from "date-fns/format";
import { MdContentPaste, MdCheckCircle, MdPending, MdTrendingUp } from "react-icons/md";
import { ItemPaginationReport } from "../../../components/ItemPaginationReport";
import { EmptyState } from "../../../components/EmptyState";
import { MultiSelectCustomerSearch } from "../../../components/MultiSelectCustomerSearch";
import { useAuth } from "../../../contexts/AuthContext";
// import { AuthContext } from "../../../contexts/AuthContext";
import { api } from "../../../services/api";

interface TechVisitReport {
  id: string;
  costumer_id: string;
  user_id: string;
  code: string;
  begin: string;
  end: string;
  responsible_id: string;
  responsible_signature: string;
  technician_id: string;
  technician_signature: string;
  observation: string;
  pending: boolean;
  sent: boolean;
  costumer: Costumer;
  user: User;
  status: string;
  type?: string;
  latitude?: number;
  longitude?: number;
  // technician: ;
  services: TechVisitReportService[];
  sectors: TechVisitReportSector[];
}

interface Costumer {
  id: string;
  name: string;
}

interface User {
  id: string;
  name: string;
}

interface TechVisitReportService {
  id: string;
  name: string;
}

interface TechVisitReportSector {
  id: string;
  sector_id: string;
  observation: string;
  sector: Sector;
  equipments: TechVisitReportSectorEquipment[];
  images: TechVisitReportSectorImage[];
}

interface TechVisitReportSectorEquipment {
  id: string;
  equipment_id: string;
  grade: string;
  equipment: Equipment;
}

interface TechVisitReportSectorImage {
  id: string;
  file_name: string;
}

interface Equipment {
  id: string;
  name: string;
}

interface Sector {
  id: string;
  costumer_id: string;
  name: string;
}

interface ReportStatus {
  total: number;
  pending: number;
  concluded: number;
}

export function TechVisitReportList() {
  const { hasPermission } = useAuth();
  // Ajuste no gerenciamento de estado e paginação infinita
  const [techVisitReports, setTechVisitReports] = useState<TechVisitReport[]>(
    [],
  );
  const [costumer_ids, setCustomerIds] = useState<string[]>([]);
  const [pending, setPending] = useState("");
  const [sent, setSent] = useState("");
  const [statusFilter, setStatusFilter] = useState("A");
  const [typeFilter, setTypeFilter] = useState("");
  const [reportStatus, setReportStatus] = useState<ReportStatus>({
    total: 0,
    pending: 0,
    concluded: 0,
  });
  const [pagination, setPagination] = useState(false);
  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [startDate, endDate] = dateRange;
  const [beginFilter, setBeginFilter] = useState(
    format(new Date(), "yyyy-MM-dd 00:00:00"),
  );
  const [endFilter, setEndFilter] = useState(
    format(new Date(), "yyyy-MM-dd 23:59:59"),
  );
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const navigate = useNavigate();

  // Função para carregar a primeira página ou reiniciar os filtros
  function paginarTechVisitReports() {
    if (loading) return;

    setLoading(true);
    setPage(1); // Reinicia a página ao carregar novos filtros
    api
      .get("/techVisitReport", {
        params: {
          costumer_id: costumer_ids.length > 0 ? costumer_ids.join(",") : undefined,
          pending,
          sent,
          status: statusFilter,
          type: typeFilter,
          begin: beginFilter,
          end: endFilter,
          page: 1,
          limit: 10,
        },
      })
      .then(response => {
        const result = response.data.data;
        setTechVisitReports(result);
        setHasMore(result.length > 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }

  // Função para carregar mais páginas ao fazer scroll
  function paginarTechVisitReportsMore() {
    if (loading || !hasMore) return;

    setLoading(true);
    api
      .get("/techVisitReport", {
        params: {
          costumer_id: costumer_ids.length > 0 ? costumer_ids.join(",") : undefined,
          pending,
          sent,
          status: statusFilter,
          type: typeFilter,
          begin: beginFilter,
          end: endFilter,
          page,
          limit: 10,
        },
      })
      .then(response => {
        const result = response.data.data;
        setTechVisitReports(prev => [...prev, ...result]);
        setHasMore(result.length > 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }

  // Carregar dados ao alterar filtros
  useEffect(() => {
    paginarTechVisitReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costumer_ids, pending, sent, statusFilter, typeFilter, beginFilter, endFilter]);

  // Carregar mais dados ao alterar a página
  useEffect(() => {
    if (page > 1) {
      paginarTechVisitReportsMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Atualizar contagem de relatórios
  useEffect(() => {
    const pendingCount = techVisitReports.filter(
      element => element.pending === false,
    ).length;
    const concludedCount = techVisitReports.filter(
      element => element.pending === true,
    ).length;

    setReportStatus({
      total: techVisitReports.length,
      pending: pendingCount,
      concluded: concludedCount,
    });
  }, [techVisitReports]);

  // Função de scroll para carregar mais dados
  const handleScroll = (e: Event) => {
    if (loading || !hasMore) return;

    const target = e.target as HTMLElement;
    const scrollPosition = target.scrollTop + target.clientHeight;
    const bottomPosition = target.scrollHeight;

    if (scrollPosition >= bottomPosition - 100) {
      setPage(prev => prev + 1);
    }
  };

  // Adicionar e remover evento de scroll no container main
  useEffect(() => {
    const mainElement = document.querySelector("main");
    if (mainElement) {
      mainElement.addEventListener("scroll", handleScroll);
      return () => mainElement.removeEventListener("scroll", handleScroll);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasMore]);



  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Card Total de Relatórios */}
        <div className="group relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-indigo-500/5 rounded-bl-full" />
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/30 group-hover:scale-110 transition-transform duration-300">
              <MdContentPaste size={24} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {reportStatus.total}
              </p>
              <p className="text-sm text-gray-500 font-medium">Relatórios</p>
            </div>
          </div>
        </div>

        {/* Card Concluídos */}
        <div className="group relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-emerald-500/5 rounded-bl-full" />
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30 group-hover:scale-110 transition-transform duration-300">
              <MdCheckCircle size={24} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {reportStatus.concluded}
              </p>
              <p className="text-sm text-gray-500 font-medium">Concluídos</p>
            </div>
          </div>
          {reportStatus.total > 0 && (
            <div className="mt-3 flex items-center gap-1.5 text-green-600">
              <MdTrendingUp size={14} />
              <span className="text-xs font-medium">
                {Math.round((reportStatus.concluded / reportStatus.total) * 100)}% do total
              </span>
            </div>
          )}
        </div>

        {/* Card Pendentes */}
        <div className="group relative bg-white rounded-2xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-amber-500/5 rounded-bl-full" />
          <div className="relative flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/30 group-hover:scale-110 transition-transform duration-300">
              <MdPending size={24} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-2xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                {reportStatus.pending}
              </p>
              <p className="text-sm text-gray-500 font-medium">Pendentes</p>
            </div>
          </div>
          {reportStatus.total > 0 && (
            <div className="mt-3 flex items-center gap-1.5 text-orange-600">
              <MdPending size={14} />
              <span className="text-xs font-medium">
                {Math.round((reportStatus.pending / reportStatus.total) * 100)}% do total
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="bg-gradient-to-r from-[#0085BD] to-[#006a96] px-6 py-4 rounded-t-2xl">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
            </svg>
            Filtros
          </h3>
        </div>
        <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Cliente(s)
            </span>
            <MultiSelectCustomerSearch
              value={costumer_ids}
              onChange={setCustomerIds}
              placeholder="Selecione clientes..."
            />
          </div>

          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Status conclusão
            </span>
            <select
              onChange={e => setPending(e.target.value)}
              className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
            >
              <option value="">Todos</option>
              <option value="true">Pendente</option>
              <option value="false">Concluído</option>
            </select>
          </div>

          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Status envio
            </span>
            <select
              onChange={e => setSent(e.target.value)}
              className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
            >
              <option value="">Todos</option>
              <option value="true">Enviado</option>
              <option value="false">Não enviado</option>
            </select>
          </div>

          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Status relatório
            </span>
            <select
              onChange={e => setStatusFilter(e.target.value)}
              defaultValue="A"
              className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
            >
              <option value="">Todos</option>
              <option value="A">Ativo</option>
              <option value="I">Inativo</option>
            </select>
          </div>

          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">
              Tipo de relatório
            </span>
            <select
              onChange={e => setTypeFilter(e.target.value)}
              className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
            >
              <option value="">Todos</option>
              <option value="technical_visit">Visita Técnica</option>
              <option value="delivery">Entrega</option>
            </select>
          </div>

          <div className="space-y-2">
            <span className="text-sm font-semibold text-gray-700">Data</span>
            <div>
              <DatePicker
                className="h-[52px] px-4 w-full rounded-lg text-gray-700 bg-white border border-gray-200 hover:border-[#0085BD] focus:border-[#0085BD] focus:ring-1 focus:ring-[#0085BD] transition-colors"
                selectsRange
                startDate={startDate}
                endDate={endDate}
                isClearable
                onChange={update => {
                  setDateRange(update);
                  const begin = moment(update[0]).format("YYYY-MM-DDT00:00:00");
                  const end = moment(update[1]).format("YYYY-MM-DDT23:59:59");

                  if (update[0] === null && update[1] === null) {
                    setBeginFilter(null);
                    setEndFilter(null);
                  }

                  if (begin <= end && update[1] !== null) {
                    setBeginFilter(begin);
                    setEndFilter(end);
                  }
                }}
                locale="ptBR"
                dateFormat="dd/MM/yyyy"
              />
            </div>
          </div>
        </div>
        </div>
      </div>

      <div className="space-y-4 mt-6">
        {techVisitReports.length > 0 ? (
          techVisitReports.map(
            ({
              id,
              costumer,
              begin,
              sent: sented,
              pending: pended,
              status,
              services,
              code,
              type,
            }) => (
              <ItemPaginationReport
                key={id}
                id={id}
                title={costumer.name}
                begin={begin}
                pending={pended}
                sent={sented}
                status={status}
                services={services}
                costumer_id={costumer.id}
                code={code}
                type={type}
                canEdit={hasPermission("tech-visit-reports:update")}
                canDelete={hasPermission("tech-visit-reports:delete")}
                setPagination={() => setPagination(!pagination)}
              />
            ),
          )
        ) : (
          <EmptyState type="reports" />
        )}

        {/* Loading inline para scroll infinito */}
        {loading && (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0085BD]" />
          </div>
        )}
      </div>
    </div>
  );
}
